import React, { useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from '@xyflow/react';
import { Zap, X } from 'lucide-react';
import { TriggerNodeData, TRIGGER_EVENT_OPTIONS, CHANNEL_OPTIONS } from '../types';

export function TriggerNode({ id, data: rawData, selected }: NodeProps) {
  const data = rawData as unknown as TriggerNodeData;
  const eventLabel = TRIGGER_EVENT_OPTIONS.find((o) => o.value === data.eventType)?.label ?? data.eventType;
  const channelLabel = CHANNEL_OPTIONS.find((o) => o.value === data.channel)?.label ?? 'Any channel';
  const [isHovered, setIsHovered] = useState(false);
  const { setNodes, setEdges } = useReactFlow();

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNodes((nodes) => nodes.filter((n) => n.id !== id));
    setEdges((edges) => edges.filter((e) => e.source !== id && e.target !== id));
  };

  return (
    <div
      className={`relative min-w-[200px] rounded-xl border-2 bg-white shadow-lg transition-all ${
        selected ? 'border-green-500 shadow-green-200' : 'border-green-400'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Delete button on hover */}
      {isHovered && (
        <button
          onClick={handleDelete}
          className="absolute -top-2 -right-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow-md hover:bg-red-600 transition-colors"
          title="Delete node"
        >
          <X size={12} />
        </button>
      )}
      {/* Header */}
      <div className="flex items-center gap-2 rounded-t-xl bg-green-500 px-3 py-2">
        <Zap size={14} className="text-white" />
        <span className="text-xs font-semibold uppercase tracking-wider text-white">Trigger</span>
      </div>
      {/* Body */}
      <div className="px-3 py-2">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{eventLabel}</p>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{channelLabel}</p>
        {data.ticketTypeName && (
          <span className="mt-1 inline-block rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
            {data.ticketTypeName}
          </span>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-green-500 !w-3 !h-3" />
    </div>
  );
}
